import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api/api';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Alert,
} from '@mui/material';
import ComputerIcon from '@mui/icons-material/Computer';
import toast from 'react-hot-toast';

// 직원 PC 키 — 개발자·직원 PC 마다 발급. 이 키가 저장된 PC 는 현장 코드 없이 원클릭 WG 까지 진행
const STATUS = {
  active: { label: '사용 가능', color: 'success' },
  expired: { label: '만료', color: 'default' },
  revoked: { label: '취소', color: 'error' },
};

function StaffKeyPanel() {
  const [name, setName] = useState('');
  const [owner, setOwner] = useState('');
  const [issued, setIssued] = useState(null);
  const [keys, setKeys] = useState([]);
  const [shared, setShared] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/field-kit/staff-keys');
      setKeys(res.data.keys || []);
      setShared(Boolean(res.data.sharedKeyEnabled));
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load staff keys');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const issue = async () => {
    if (!name.trim()) return toast.error('PC 이름을 입력하세요');
    setBusy(true);
    try {
      const res = await api.post('/field-kit/staff-keys', { name, owner });
      setIssued(res.data);
      setName('');
      setOwner('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Issue failed');
    }
    setBusy(false);
  };

  const revoke = async (id) => {
    if (!window.confirm('이 PC 키를 취소할까요? 그 PC 는 다시 현장 코드가 필요합니다.')) return;
    try {
      await api.post(`/field-kit/staff-keys/${id}/revoke`);
      if (issued?.id === id) setIssued(null);
      toast.success('Revoked');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Revoke failed');
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('복사됨');
    } catch (_) {
      toast.error('복사 실패 — 직접 선택해 복사하세요');
    }
  };

  const fmt = (d) =>
    d ? new Date(d).toLocaleString(undefined, { year: '2-digit', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <ComputerIcon sx={{ color: '#551FFF' }} />
        <Typography sx={{ fontSize: 16, fontWeight: 700 }}>직원 PC 키 (Staff PC key)</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Digntec 직원·개발자 PC 마다 발급합니다. 키를 저장한 PC 는 현장 코드 없이 원클릭이 WireGuard 까지 진행합니다.
        PC 에는 암호화해 저장합니다 (Windows: 그 사용자 계정에서만 풀림 · Mac: 키체인). <b>매장 PC 에는 절대 저장하지 마세요.</b>
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
        <TextField size="small" label="PC 이름 (예: Ken MacBook)" value={name} onChange={(e) => setName(e.target.value)} sx={{ minWidth: 220 }} />
        <TextField size="small" label="사용자 (이메일·이름)" value={owner} onChange={(e) => setOwner(e.target.value)} sx={{ flex: 1 }} />
        <Button variant="contained" onClick={issue} disabled={busy} sx={{ whiteSpace: 'nowrap' }}>
          키 발급
        </Button>
      </Stack>

      {issued && (
        <Alert severity="warning" sx={{ mb: 1.5, '& .MuiAlert-message': { width: '100%', minWidth: 0 } }} onClose={() => setIssued(null)}>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {issued.name} — 이 화면을 닫으면 다시 볼 수 없습니다. 그 PC 에서 아래 명령으로 바로 저장하세요.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
            <Typography sx={{ fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all', flex: 1 }}>{issued.key}</Typography>
            <Button size="small" variant="outlined" onClick={() => copy(issued.key)}>복사</Button>
          </Box>
          <Typography variant="caption" display="block">
            Windows (키트 폴더 PowerShell): <code>.\tlj-apk-update.ps1 -SaveStaffKey</code> → 키 붙여넣기
          </Typography>
          <Typography variant="caption" display="block">
            Mac (키트 폴더 터미널): <code>bash tlj-apk-update-mac.sh --save-staff-key</code> → 키 붙여넣기
          </Typography>
        </Alert>
      )}

      {shared && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          공용 키(FIELD_KIT_API_KEY)도 아직 켜져 있습니다 — 모든 직원 PC 에 개인 키를 설정한 뒤 서버에서 FIELD_KIT_SHARED_KEY=off 로 끕니다.
        </Typography>
      )}

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 720, '& th': { whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow>
              <TableCell>PC</TableCell>
              <TableCell>사용자</TableCell>
              <TableCell>키</TableCell>
              <TableCell>마지막 사용</TableCell>
              <TableCell>만료</TableCell>
              <TableCell>상태</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary', py: 2 }}>
                  발급된 PC 키 없음
                </TableCell>
              </TableRow>
            )}
            {keys.map((k) => {
              const st = STATUS[k.status] || { label: k.status, color: 'default' };
              return (
                <TableRow key={k._id}>
                  <TableCell>
                    {k.name}
                    <Typography variant="caption" color="text.secondary" display="block">
                      {fmt(k.createdAt)} · {k.issuedBy?.email || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>{k.owner || '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>dfk_…{k.keyHint}</TableCell>
                  <TableCell>
                    {fmt(k.lastUsedAt)}
                    {k.lastUsedAt ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {k.lastUsedIp || ''} · {k.useCount}회
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{fmt(k.expiresAt)}</TableCell>
                  <TableCell>
                    <Chip size="small" color={st.color} label={st.label} />
                  </TableCell>
                  <TableCell align="right">
                    {k.status === 'active' && (
                      <Button size="small" color="error" onClick={() => revoke(k._id)}>
                        취소
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default StaffKeyPanel;
